import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import FormRenderer from '../components/form-connect/FormRenderer';
import { Loader, ArrowLeft, Share2, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const FormPreview = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQR, setShowQR] = useState(false);

  // Fetch form data
  useEffect(() => {
    const fetchForm = async () => {
      try {
        setLoading(true);
        
        const formRef = doc(db, 'forms', formId);
        const formSnap = await getDoc(formRef);
        
        if (formSnap.exists()) {
          setForm({
            id: formSnap.id,
            ...formSnap.data()
          });
        } else {
          setError('Form not found');
        }
      } catch (error) {
        console.error('Error fetching form:', error);
        setError('Error loading form. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    if (formId) {
      fetchForm();
    }
  }, [formId]);

  // Generate form URL
  const getFormUrl = () => {
    return `${window.location.origin}/form/${formId}`;
  };

  // Copy form URL to clipboard
  const copyFormUrl = () => {
    const url = getFormUrl();
    navigator.clipboard.writeText(url);
    alert('Form URL copied to clipboard!');
  };

  // Toggle QR code display
  const toggleQR = () => {
    setShowQR(!showQR);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <Loader className="animate-spin h-8 w-8 text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading form preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">{error}</h3>
          <div className="mt-6">
            <button
              onClick={() => navigate('/form-connect')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Back to Forms
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center mb-4 sm:mb-0">
            <button
              onClick={() => navigate('/form-connect')}
              className="mr-4 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Form Preview</h1>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={copyFormUrl}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Copy Link
            </button>
            <button
              onClick={toggleQR}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <QrCode className="h-4 w-4 mr-2" />
              {showQR ? 'Hide QR Code' : 'Show QR Code'}
            </button>
          </div>
        </div>
        
        {showQR && (
          <div className="mb-6 bg-white p-6 rounded-lg shadow-md text-center">
            <h2 className="text-lg font-medium text-gray-900 mb-4">QR Code for Form</h2>
            <div className="inline-block p-4 bg-white rounded-lg">
              <QRCodeSVG value={getFormUrl()} size={200} />
            </div>
            <p className="mt-4 text-sm text-gray-500">
              Scan this QR code to open the form on a mobile device
            </p>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="bg-white p-4 rounded-lg shadow-md mb-4">
              <h2 className="text-lg font-medium text-gray-900 mb-2">Form Details</h2>
              <div className="text-sm text-gray-600">
                <p><span className="font-medium">Name:</span> {form.name}</p>
                {form.description && (
                  <p className="mt-1"><span className="font-medium">Description:</span> {form.description}</p>
                )}
                <p className="mt-1"><span className="font-medium">Fields:</span> {form.fields.length}</p>
                <p className="mt-1">
                  <span className="font-medium">WhatsApp Number:</span> {form.settings?.whatsappNumber || 'Not set'}
                </p>
              </div>
            </div>
            
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
              <h2 className="text-lg font-medium text-indigo-900 mb-2">Preview Notes</h2>
              <ul className="text-sm text-indigo-700 space-y-2">
                <li>• This is a preview of how your form will appear to users</li>
                <li>• Form submissions in preview mode are simulated</li>
                <li>• Share the form link or QR code for others to access the live form</li>
                <li>• Make sure your WhatsApp number is correct before sharing</li>
              </ul>
            </div>
          </div>
          
          <div>
            <FormRenderer form={form} isPreview={true} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormPreview;
